import api from '../../../services/api';
import { submitOneTimeDonation } from './submitOneTimeDonation';
import { submitSubscriptionConfirm } from './submitSubscriptionConfirm';
import { createDonationSchedule } from './createDonationSchedule';
import { createForeverSubscription } from './createForeverSubscription';

// handleSubmit for useDonationForm, extracted VERBATIM: validation, the
// initial one-time-intent-creation-if-needed block, and dispatch to whichever
// of the 4 extracted submit/create branches applies. The
// createDonationSchedule/createForeverSubscription branches (handleSubmit's
// own fallback for when no clientSecret exists yet) are, per the current UI
// wiring (DonationPaymentSection only renders the "Donate" submit button once
// showPaymentForm is true, which itself requires clientSecret to already be
// set via handleProceedToPayment), not reachable through normal interaction —
// but this is a UI-wiring inference, not confirmed-dead code the way a
// grep-verified unused ref/function is, so both branches are preserved
// verbatim rather than dropped.
export function useDonationSubmit({
  donationType,
  preservedOneTimeAmount,
  oneTimeAmount,
  setOneTimeAmount,
  preservedMonthlyAmount,
  monthlyAmount,
  setMonthlyAmount,
  preservedIsForever,
  isForever,
  setPreservedIsForever,
  preservedNumberOfPayments,
  numberOfPayments,
  setNumberOfPayments,
  setPreservedMonthlyAmount,
  setPreservedNumberOfPayments,
  user,
  firstName,
  lastName,
  email,
  phone,
  donationFor,
  stripe,
  elements,
  clientSecret,
  clientSecretFromParent,
  showPaymentElement,
  paymentIntentId,
  setPaymentIntentId,
  onClientSecretChange,
  setClientSecret,
  setPreservedDonationType,
  preservedDonationType,
  subscriptionData,
  preservedSubscriptionDataFromParent,
  setSubscriptionData,
  setPreservedSubscriptionDataFromParent,
  setShowPaymentElement,
  setPreservedOneTimeAmount,
  navigate,
  setError,
  setSuccess,
  setLoading,
}) {
  const handleSubmit = async (e) => {
    e.preventDefault();

    setError('');
    setSuccess('');

    // Validate inputs based on donation type
    if (donationType === 'one-time') {
      const amountToCheck = preservedOneTimeAmount || parseFloat(oneTimeAmount);
      if (!amountToCheck || amountToCheck <= 0) {
        setError('Please enter a valid one-time donation amount.');
        return;
      }
    } else {
      // Monthly donation - ALWAYS use preserved values if payment element is already shown
      // Otherwise use form values, but also preserve them immediately
      const monthlyAmt = (showPaymentElement && preservedMonthlyAmount)
        ? preservedMonthlyAmount
        : (preservedMonthlyAmount || parseFloat(monthlyAmount));
      const forever = (showPaymentElement && preservedIsForever !== null)
        ? preservedIsForever
        : (preservedIsForever || isForever);
      const payments = (showPaymentElement && preservedNumberOfPayments)
        ? preservedNumberOfPayments
        : (preservedNumberOfPayments || numberOfPayments);

      // If we have form values but not preserved values, preserve them NOW
      if (!preservedMonthlyAmount && monthlyAmount) {
        setPreservedMonthlyAmount(parseFloat(monthlyAmount));
      }
      if (!preservedNumberOfPayments && numberOfPayments && !isForever) {
        setPreservedNumberOfPayments(parseInt(numberOfPayments));
      }
      if (preservedIsForever === false && isForever) {
        setPreservedIsForever(true);
      }

      // Monthly donation - amount is required
      if (!monthlyAmt || monthlyAmt <= 0 || isNaN(monthlyAmt)) {
        setError('Monthly donation amount is required. Please enter a valid amount.');
        return;
      }
      // Number of payments or unlimited option is required
      if (!forever && (!payments || payments === 'forever' || (typeof payments === 'string' && payments.trim() === '') || (typeof payments === 'number' ? payments < 1 : parseInt(payments) < 1) || (typeof payments === 'number' ? payments > 120 : parseInt(payments) > 120))) {
        setError('Number of monthly payments is required. Please enter a number between 1 and 120, or select "Unlimited".');
        return;
      }
    }

    if (!user && (!firstName || !lastName)) {
      setError('Please fill in all required fields (First Name, Last Name).');
      return;
    }

    if (!stripe) {
      setError('Stripe is not loaded. Please refresh the page.');
      return;
    }

    // If we have a one-time payment and no clientSecret yet, create payment intent first
    // Check both oneTimeAmount and preservedOneTimeAmount
    const amountToUse = preservedOneTimeAmount || parseFloat(oneTimeAmount);
    if (donationType === 'one-time' && amountToUse && !clientSecret && !clientSecretFromParent) {
      setLoading(true);
      try {
        const amount = amountToUse;
        const response = await api.publicRequest('/payments/create-donation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(user && { Authorization: `Bearer ${localStorage.getItem('faithinsight_token')}` })
          },
          body: JSON.stringify({
            oneTimeAmount: amount,
            firstName,
            lastName,
            email,
            ...(phone && phone.trim() && { phone: phone.trim() }),
            // Best Practice: Include existing PaymentIntent ID to reuse if checkout was interrupted
            ...(paymentIntentId && { existingPaymentIntentId: paymentIntentId }),
          })
        });

        if (response.success && response.oneTimePayment) {
          setPreservedOneTimeAmount(amount); // Preserve the amount
          // Best Practice: Store clientSecret securely - never log it or expose in URLs
          const newClientSecret = response.oneTimePayment.clientSecret;

          // CRITICAL: Update parent FIRST via callback, then update local state
          // This ensures Elements remounts with the new clientSecret before we show PaymentElement
          if (onClientSecretChange) {
            onClientSecretChange(newClientSecret);
          }

          setClientSecret(newClientSecret);

          // Best Practice: Store paymentIntentId to allow reusing PaymentIntent if checkout is interrupted
          setPaymentIntentId(response.oneTimePayment.paymentIntentId);
          // Preserve donation type to prevent reset on Elements remount
          setPreservedDonationType('one-time');
          setShowPaymentElement(true);
          setLoading(false);
          return; // Wait for user to complete payment
        } else {
          setError(response.message || 'Failed to create payment intent. Please try again.');
          setLoading(false);
        }
      } catch (err) {
        setError(err.message || 'Failed to create payment. Please try again.');
        setLoading(false);
        return;
      }
    }

    // If we have clientSecret and payment element, confirm the payment
    if (clientSecret && stripe && elements) {
      setLoading(true);
      try {
        // First, submit the elements to validate the form
        const { error: submitError } = await elements.submit();
        if (submitError) {
          setError(submitError.message || 'Please check your payment information.');
          setLoading(false);
          return;
        }

        // Check if this is a one-time donation or subscription
        // Use preserved subscription data if local state was lost on remount
        // Also check preserved donation type to determine payment type
        const activeSubscriptionData = subscriptionData || preservedSubscriptionDataFromParent;
        const isSubscriptionPayment = !!activeSubscriptionData || preservedDonationType === 'monthly';

        if (isSubscriptionPayment) {
          await submitSubscriptionConfirm({
            stripe,
            elements,
            clientSecret,
            preservedMonthlyAmount,
            monthlyAmount,
            preservedNumberOfPayments,
            numberOfPayments,
            preservedIsForever,
            isForever,
            subscriptionData,
            preservedSubscriptionDataFromParent,
            firstName,
            lastName,
            email,
            navigate,
            setError,
            setLoading,
          });
        } else {
          await submitOneTimeDonation({
            stripe,
            elements,
            clientSecret,
            preservedOneTimeAmount,
            oneTimeAmount,
            email,
            firstName,
            lastName,
            user,
            navigate,
            setError,
            setLoading,
          });
        }
      } catch (err) {
          setError(err.message || 'An error occurred during payment confirmation.');
          setLoading(false);
      }
    } else if (donationType === 'monthly') {
      // Monthly donation - use PaymentMethod approach for subscriptions
      // Use preserved values if available (in case form was already submitted once)
      const monthlyAmt = preservedMonthlyAmount || parseFloat(monthlyAmount);
      const forever = preservedIsForever || isForever;
      const payments = preservedNumberOfPayments || numberOfPayments;

      // Validate before making API call
      if (!monthlyAmt || monthlyAmt <= 0) {
        setError('Monthly donation amount is required. Please enter a valid amount.');
        setLoading(false);
        return;
      }

      if (!forever && (!payments || (typeof payments === 'string' && payments.trim() === '') || (typeof payments === 'number' ? payments < 1 : parseInt(payments) < 1))) {
        setError('Number of monthly payments is required. Please enter a positive number, or select "Unlimited".');
        setLoading(false);
        return;
      }

      // For fixed-term monthly donations (not forever), use the new PaymentMethod approach
      if (!forever && payments && parseInt(payments) >= 1) {
        await createDonationSchedule({
          elements,
          stripe,
          monthlyAmt,
          payments,
          email,
          firstName,
          lastName,
          user,
          donationFor,
          navigate,
          setError,
          setLoading,
        });
        return;
      }

      // For "forever" subscriptions or payments > 36, use the old PaymentIntent approach
      await createForeverSubscription({
        monthlyAmt,
        forever,
        payments,
        firstName,
        lastName,
        email,
        phone,
        donationFor,
        user,
        monthlyAmount,
        setMonthlyAmount,
        numberOfPayments,
        setNumberOfPayments,
        setPreservedMonthlyAmount,
        setPreservedNumberOfPayments,
        setPreservedIsForever,
        setPreservedOneTimeAmount,
        setPreservedDonationType,
        setSubscriptionData,
        setPreservedSubscriptionDataFromParent,
        setClientSecret,
        setShowPaymentElement,
        setError,
        setLoading,
      });
    }
  };

  return { handleSubmit };
}
